import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, RefreshCw, Loader2, ExternalLink, Info } from 'lucide-react';
import { getDetailedWeather } from '../services/geminiService';

const WeatherWidget: React.FC = () => {
  const [weatherData, setWeatherData] = useState<{text: string, sources: any[]} | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchWeather = async () => {
    setLoading(true);
    const data = await getDetailedWeather();
    setWeatherData(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getWeatherIcon = (text: string) => {
    const lower = text.toLowerCase();
    if (lower.includes('rain')) return <CloudRain className="w-10 h-10 text-sky-500" strokeWidth={1} />;
    if (lower.includes('cloud')) return <Cloud className="w-10 h-10 text-slate-300" strokeWidth={1} />;
    return <Sun className="w-10 h-10 text-amber-400" strokeWidth={1} />;
  };

  const renderFormattedText = (text: string) => {
    return text.split('\n').map((line, i) => {
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <div key={i} className="mb-5 last:mb-0">
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j} className="text-slate-900 font-bold block mb-1 tracking-tight text-base md:text-lg not-italic font-sans uppercase">{part.replace(/\*\*/g, '')}</strong>;
            }
            return <span key={j} className="text-slate-500/80 text-sm md:text-base leading-relaxed">{part}</span>;
          })}
        </div>
      );
    });
  };

  return (
    <div className="relative w-full py-4 animate-in fade-in duration-1000">
      <div className="flex justify-center mb-10">
        <div className="bg-slate-900 text-white px-7 py-2.5 rounded-full text-[8px] font-bold tracking-luxury uppercase shadow-lg flex flex-col items-center">
          <span className="leading-tight">Local Weather</span>
          <span className="leading-tight">Station Updates</span>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-10 lg:gap-20">
        <div className="w-full md:w-4/12 flex items-center gap-6">
          <div className="flex-shrink-0 p-6 bg-white rounded-[2rem] border border-slate-100 flex items-center justify-center shadow-sm">
            {loading ? (
              <Loader2 className="w-10 h-10 text-sky-100 animate-spin" />
            ) : (
              getWeatherIcon(weatherData?.text || '')
            )}
          </div>
          
          <div className="pt-1">
            <h3 className="text-3xl font-serif font-bold text-slate-900 leading-[0.8] tracking-tighter">
              Current
            </h3>
            <h3 className="text-3xl font-serif font-light text-sky-500 italic leading-[0.8] mt-1.5">
              Conditions
            </h3>
            <div className="mt-4 space-y-0.5">
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.2em] block">Sarangani, Davao Occi</span>
            </div>
          </div>
        </div>

        <div className="w-full md:w-8/12">
          <div className="relative pl-8 md:pl-10 border-l border-slate-100 py-1">
            {loading ? (
              <div className="space-y-4 py-2">
                <div className="h-6 bg-slate-50 rounded-full w-1/4 animate-pulse"></div>
                <div className="h-4 bg-slate-50 rounded-full w-full animate-pulse"></div>
              </div>
            ) : (
              <div className="text-slate-600 font-serif italic text-lg md:text-xl leading-relaxed tracking-tight">
                {renderFormattedText(weatherData?.text || '')}
              </div>
            )}

            <div className="mt-10 flex flex-col gap-6">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                <button 
                  onClick={fetchWeather}
                  className="flex items-center gap-2.5 text-[9px] font-bold uppercase tracking-luxury text-slate-400 hover:text-sky-500 transition-all group"
                >
                  <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-1000'}`} />
                  Refresh Report
                </button>
                
                {!loading && (
                  <div className="flex items-center gap-2">
                      <div className="h-2.5 w-px bg-slate-100 hidden sm:block"></div>
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-luxury flex items-center gap-2.5">
                          <Info className="w-3 h-3" /> 
                          Verified Local Data
                      </span>
                  </div>
                )}
              </div>

              {!loading && weatherData?.sources && weatherData.sources.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-2 pt-2 border-t border-slate-50">
                  {weatherData.sources.map((chunk: any, i: number) => {
                    const web = chunk.web || chunk.search_entry;
                    if (!web?.uri) return null;
                    return (
                      <a 
                        key={i} 
                        href={web.uri} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="text-[9px] text-slate-400 hover:text-sky-500 flex items-center gap-1.5 transition-colors font-medium"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {web.title || 'Official Source'}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherWidget;